/**
 * Translation utility for CRM UI labels
 * Supports: English (default), Arabic (AR), Hebrew (HE)
 */

import type { Lang } from './lang';

type TranslationDict = Record<string, string>;

const translations: Record<Lang, TranslationDict> = {
  en: {
    // Navigation
    'nav.dashboard': 'Dashboard',
    'nav.leads': 'Leads',
    'nav.pipeline': 'Pipeline',
    'nav.products': 'Products',
    'nav.orders': 'Orders',
    'nav.campaigns': 'Campaigns',
    'nav.templates': 'Templates',
    'nav.messages': 'Messages',
    'nav.calls': 'Calls',
    'nav.reports': 'Reports',
    'nav.settings': 'Settings',
    'nav.logout': 'Logout',

    // Login
    'login.logging_in': 'Logging in...',
    'login.success': 'Login successful!',
    'login.failed': 'Login failed',
    'login.error': 'Connection error',
    'login.subtitle': 'Customer Relationship Management',

    // Actions
    'action.save': 'Save',
    'action.cancel': 'Cancel',
    'action.delete': 'Delete',
    'action.edit': 'Edit',
    'action.add': 'Add',
    'action.search': 'Search',
    'action.filter': 'Filter',
    'action.export': 'Export',
    'action.import': 'Import',
    'action.refresh': 'Refresh',
    'action.send': 'Send',
    'action.call': 'Call',
    'action.whatsapp': 'WhatsApp',
    'action.back': 'Back',
    'action.view': 'View',
    'action.open': 'Open',
    'action.done': 'Done',
    'action.retry': 'Retry',
    'action.add_lead': 'Add Lead',
    'action.copy_phone': 'Copy Phone',

    // Leads page
    'leads.search_placeholder': 'Search by name or phone...',
    'leads.no_leads': 'No leads found',
    'leads.add_first': 'Add First Lead',
    'leads.add_new': 'Add New Lead',
    'leads.last_activity': 'Last Activity',
    'leads.actions': 'Actions',
    'leads.no_name': 'No Name',

    // Sources
    'source.website': 'Website',
    'source.whatsapp': 'WhatsApp',
    'source.referral': 'Referral',
    'source.ad': 'Advertisement',
    'source.other': 'Other',

    // Stages
    'stage.all': 'All Stages',
    'stage.new': 'New',
    'stage.contacted': 'Contacted',
    'stage.qualified': 'Qualified',
    'stage.proposal': 'Proposal',
    'stage.won': 'Won',
    'stage.lost': 'Lost',

    // Status / States
    'status.loading': 'Loading...',
    'status.error': 'Error',
    'status.empty': 'No data',
    'status.open': 'Open',
    'status.done': 'Completed',
    'status.cancelled': 'Cancelled',
    'status.sent': 'Sent',
    'status.delivered': 'Delivered',
    'status.read': 'Read',
    'status.failed': 'Failed',
    'status.pending': 'Pending',
    'status.preview': 'Preview',
    'status.overdue': 'Overdue',

    // Forms
    'form.name': 'Name',
    'form.phone': 'Phone',
    'form.email': 'Email',
    'form.notes': 'Notes',
    'form.required': 'Required',
    'form.title': 'Title',
    'form.description': 'Description',
    'form.date': 'Date',
    'form.time': 'Time',
    'form.select': 'Select...',

    // Messages / Toasts
    'msg.saved': 'Saved successfully',
    'msg.deleted': 'Deleted successfully',
    'msg.error': 'Error, please try again',
    'msg.confirm_delete': 'Are you sure you want to delete?',
    'msg.task_added': 'Task added successfully',
    'msg.task_done': 'Task completed',
    'msg.task_cancelled': 'Task cancelled',
    'msg.message_sent': 'Message sent',
    'msg.call_logged': 'Call logged',
    'msg.note_added': 'Note added',
    'msg.stage_updated': 'Stage updated',
    'msg.lead_added': 'Lead added successfully',
    'msg.lead_add_failed': 'Failed to add lead',
    'msg.copied': 'Copied',
    'msg.auto_message_sent_today': 'Automatic message already sent today',
    'msg.auto_message_scheduled': 'Automatic message scheduled',

    // Lead Detail
    'lead.potential_customer': 'Potential Customer',
    'lead.contact_whatsapp': 'WhatsApp Contact',
    'lead.quick_followup': 'Quick Follow-up',
    'lead.info': 'Lead Info',
    'lead.phone': 'Phone',
    'lead.email': 'Email',
    'lead.source': 'Source',
    'lead.stage': 'Stage',
    'lead.created_at': 'Created',
    'lead.updated_at': 'Updated',
    'lead.value': 'Value',
    'lead.notes': 'Notes',
    'lead.no_leads': 'No leads found',
    'lead.load_error': 'Failed to load lead data',
    'lead.not_found': 'Lead not found',
    'lead.not_found_desc': 'Cannot find this lead',

    // Tabs
    'tab.timeline': 'Timeline',
    'tab.notes': 'Notes',
    'tab.tasks': 'Tasks',
    'tab.messages': 'Messages',
    'tab.calls': 'Calls',
    'tab.orders': 'Orders',
    'tab.attribution': 'Attribution',

    // Timeline
    'timeline.no_events': 'No events',
    'timeline.task_done': 'Task completed',
    'timeline.task_cancelled': 'Task cancelled',
    'timeline.task_created': 'Task created',
    'timeline.call_logged': 'Call logged',
    'timeline.message_sent': 'Message sent',
    'timeline.stage_changed': 'Stage changed',
    'timeline.note_added': 'Note added',
    'timeline.order_created': 'Order created',

    // Tasks
    'task.priority.urgent': 'Urgent',
    'task.priority.high': 'High',
    'task.priority.normal': 'Normal',
    'task.priority.low': 'Low',
    'task.channel.call': 'Call',
    'task.channel.whatsapp': 'WhatsApp',
    'task.channel.sms': 'SMS',
    'task.due_at': 'Due',
    'task.add': 'Add Task',
    'task.no_tasks': 'No tasks',

    // Calls
    'call.outcome': 'Outcome',
    'call.outcome.answered': 'Answered',
    'call.outcome.no_answer': 'No Answer',
    'call.outcome.busy': 'Busy',
    'call.outcome.voicemail': 'Voicemail',
    'call.outcome.wrong_number': 'Wrong Number',
    'call.outcome.other': 'Other',
    'call.duration': 'Duration (seconds)',
    'call.minutes': 'minutes',
    'call.log': 'Log Call',
    'call.no_calls': 'No calls',

    // Messages
    'message.send': 'Send Message',
    'message.template': 'Template',
    'message.free_text': 'Free Text',
    'message.no_messages': 'No messages',
    'message.quick_templates': 'Quick Templates',

    // Attribution
    'attribution.source': 'Source',
    'attribution.campaign': 'Campaign',
    'attribution.medium': 'Medium',
    'attribution.landing_page': 'Landing Page',
    'attribution.referrer': 'Referrer',
    'attribution.no_data': 'No attribution data',
    'attribution.visits': 'Campaign visits will appear here',
    'attribution.event_type': 'Event Type',
    'attribution.created': 'Created',
    'attribution.visit': 'Visit',
    'attribution.source_data': 'Source Data',
    'attribution.first_visit': 'First Visit',
    'attribution.visit_log': 'Visit Log',

    // Orders
    'order.total': 'Total',
    'order.items': 'Items',
    'order.status': 'Status',
    'order.date': 'Date',
    'order.no_orders': 'No orders',
    'order.view': 'View Order',

    // Quick Actions
    'quick.log_call': 'Log Call',
    'quick.send_message': 'Send Message',
    'quick.add_note': 'Add Note',
    'quick.add_task': 'Add Task',
    'quick.templates': 'Quick Templates',

    // Errors
    'error.load_failed': 'Failed to load',
    'error.save_failed': 'Failed to save',
    'error.network': 'Network error',
    'error.unauthorized': 'Unauthorized',
    'error.forbidden': 'Forbidden',
    'error.not_found': 'Not Found',
    'error.server': 'Server Error',
    'error.login_required': 'Please log in to continue',
    'error.no_access': 'You do not have access',
    'error.not_found_desc': 'Content not found',
    'error.code': 'Error Code',
    'error.try_again': 'Please try again',

    // Pipeline
    'pipeline.title': 'Sales Pipeline',
    'pipeline.drag_drop': 'Drag and drop leads between stages',
    'pipeline.empty_stage': 'No leads in this stage',

    // Dashboard
    'dashboard.title': 'Dashboard',
    'dashboard.total_leads': 'Total Leads',
    'dashboard.new_leads': 'New Leads',
    'dashboard.conversion_rate': 'Conversion Rate',
    'dashboard.revenue': 'Revenue',
    'dashboard.recent_leads': 'Recent Leads',
    'dashboard.tasks_today': 'Tasks for Today',
    'dashboard.tasks': 'Tasks',
    'dashboard.overdue': 'Overdue',
    'dashboard.today': 'Today',
    'dashboard.upcoming': 'Upcoming',
    'dashboard.no_overdue_tasks': 'No overdue tasks',
    'dashboard.no_tasks_today': 'No tasks for today',
    'dashboard.hot_leads': 'Hot Leads',
    'dashboard.pipeline_overview': 'Pipeline Overview',
    'dashboard.calls_today': 'Calls Today',

    // Actions (additional)
    'action.view_all': 'View All',

    // General
    'general.and': 'and',
    'general.or': 'or',
    'general.of': 'of',
    'general.yes': 'Yes',
    'general.no': 'No',
    'general.all': 'All',
    'general.none': 'None',

    // Reports Page
    'reports.title': 'Reports & Analytics',
    'reports.total_leads': 'Total Leads',
    'reports.converted': 'Converted',
    'reports.messages_sent': 'Messages Sent',
    'reports.calls_made': 'Calls Made',
    'reports.date_range': 'Date Range',
    'reports.last_7_days': 'Last 7 Days',
    'reports.last_30_days': 'Last 30 Days',
    'reports.last_90_days': 'Last 90 Days',
    'reports.custom': 'Custom',

    // Settings Page
    'settings.title': 'Settings',
    'settings.general': 'General',
    'settings.notifications': 'Notifications',
    'settings.profile': 'Profile',
    'settings.language': 'Language',
    'settings.timezone': 'Timezone',
    'settings.save_changes': 'Save Changes',
    'settings.email_notifications': 'Email Notifications',
    'settings.push_notifications': 'Push Notifications',
    'settings.sms_alerts': 'SMS Alerts',

    // Templates Page
    'templates.title': 'Message Templates',
    'templates.all_channels': 'All Channels',
    'templates.all_languages': 'All Languages',
    'templates.no_templates': 'No templates found',
    'templates.add_new': 'Add Template',

    // Campaigns Page
    'campaigns.title': 'Campaigns',
    'campaigns.all_statuses': 'All Statuses',
    'campaigns.active': 'Active',
    'campaigns.paused': 'Paused',
    'campaigns.draft': 'Draft',
    'campaigns.completed': 'Completed',
    'campaigns.no_campaigns': 'No campaigns found',
    'campaigns.add_new': 'New Campaign',
    'campaigns.sent': 'Sent',
    'campaigns.opened': 'Opened',
    'campaigns.clicked': 'Clicked',
  },

  ar: {
    // Navigation
    'nav.dashboard': 'لوحة التحكم',
    'nav.leads': 'العملاء المحتملين',
    'nav.pipeline': 'مسار المبيعات',
    'nav.products': 'المنتجات',
    'nav.orders': 'الطلبات',
    'nav.campaigns': 'الحملات',
    'nav.templates': 'القوالب',
    'nav.messages': 'الرسائل',
    'nav.calls': 'المكالمات',
    'nav.reports': 'التقارير',
    'nav.settings': 'الإعدادات',
    'nav.logout': 'تسجيل الخروج',

    // Login
    'login.logging_in': 'جاري تسجيل الدخول...',
    'login.success': 'تم تسجيل الدخول بنجاح!',
    'login.failed': 'فشل تسجيل الدخول',
    'login.error': 'خطأ في الاتصال',
    'login.subtitle': 'إدارة علاقات العملاء',

    // Actions
    'action.save': 'حفظ',
    'action.cancel': 'إلغاء',
    'action.delete': 'حذف',
    'action.edit': 'تعديل',
    'action.add': 'إضافة',
    'action.search': 'بحث',
    'action.filter': 'تصفية',
    'action.export': 'تصدير',
    'action.import': 'استيراد',
    'action.refresh': 'تحديث',
    'action.send': 'إرسال',
    'action.call': 'اتصال',
    'action.whatsapp': 'واتساب',
    'action.back': 'رجوع',
    'action.view': 'عرض',
    'action.open': 'فتح',
    'action.done': 'تم',
    'action.retry': 'إعادة المحاولة',
    'action.add_lead': 'إضافة عميل',
    'action.copy_phone': 'نسخ الرقم',

    // Leads page
    'leads.search_placeholder': 'بحث بالاسم أو الهاتف...',
    'leads.no_leads': 'لا يوجد عملاء محتملين',
    'leads.add_first': 'إضافة أول عميل',
    'leads.add_new': 'إضافة عميل جديد',
    'leads.last_activity': 'آخر نشاط',
    'leads.actions': 'إجراءات',
    'leads.no_name': 'بدون اسم',

    // Sources
    'source.website': 'موقع الويب',
    'source.whatsapp': 'واتساب',
    'source.referral': 'إحالة',
    'source.ad': 'إعلان',
    'source.other': 'آخر',

    // Stages
    'stage.all': 'جميع المراحل',
    'stage.new': 'جديد',
    'stage.contacted': 'تم التواصل',
    'stage.qualified': 'مؤهل',
    'stage.proposal': 'عرض سعر',
    'stage.won': 'مكتسب',
    'stage.lost': 'مفقود',

    // Status / States
    'status.loading': 'جاري التحميل...',
    'status.error': 'خطأ',
    'status.empty': 'لا توجد بيانات',
    'status.open': 'مفتوحة',
    'status.done': 'مكتملة',
    'status.cancelled': 'ملغاة',
    'status.sent': 'تم الإرسال',
    'status.delivered': 'تم التسليم',
    'status.read': 'تمت القراءة',
    'status.failed': 'فشل',
    'status.pending': 'قيد الانتظار',
    'status.preview': 'معاينة',
    'status.overdue': 'متأخرة',

    // Forms
    'form.name': 'الاسم',
    'form.phone': 'الهاتف',
    'form.email': 'البريد الإلكتروني',
    'form.notes': 'ملاحظات',
    'form.required': 'مطلوب',
    'form.title': 'العنوان',
    'form.description': 'الوصف',
    'form.date': 'التاريخ',
    'form.time': 'الوقت',
    'form.select': 'اختر...',

    // Messages / Toasts
    'msg.saved': 'تم الحفظ بنجاح',
    'msg.deleted': 'تم الحذف بنجاح',
    'msg.error': 'حدث خطأ، حاول مرة أخرى',
    'msg.confirm_delete': 'هل أنت متأكد من الحذف؟',
    'msg.task_added': 'تمت إضافة المهمة بنجاح',
    'msg.task_done': 'تم إنجاز المهمة',
    'msg.task_cancelled': 'تم إلغاء المهمة',
    'msg.message_sent': 'تم إرسال الرسالة',
    'msg.call_logged': 'تم تسجيل المكالمة',
    'msg.note_added': 'تمت إضافة الملاحظة',
    'msg.stage_updated': 'تم تحديث المرحلة',
    'msg.lead_added': 'تم إضافة العميل بنجاح',
    'msg.lead_add_failed': 'فشل إضافة العميل',
    'msg.copied': 'تم النسخ',
    'msg.auto_message_sent_today': 'تم إرسال رسالة تلقائية مسبقاً اليوم',
    'msg.auto_message_scheduled': 'تم جدولة رسالة تلقائية',

    // Lead Detail
    'lead.potential_customer': 'عميل محتمل',
    'lead.contact_whatsapp': 'تواصل واتساب',
    'lead.quick_followup': 'متابعة سريعة',
    'lead.info': 'معلومات العميل',
    'lead.phone': 'الهاتف',
    'lead.email': 'البريد',
    'lead.source': 'المصدر',
    'lead.stage': 'المرحلة',
    'lead.created_at': 'تاريخ الإنشاء',
    'lead.updated_at': 'آخر تحديث',
    'lead.value': 'القيمة',
    'lead.notes': 'ملاحظات',
    'lead.no_leads': 'لا يوجد عملاء',
    'lead.load_error': 'فشل تحميل بيانات العميل',
    'lead.not_found': 'العميل غير موجود',
    'lead.not_found_desc': 'لا يمكن العثور على هذا العميل',

    // Tabs
    'tab.timeline': 'السجل',
    'tab.notes': 'الملاحظات',
    'tab.tasks': 'المهام',
    'tab.messages': 'الرسائل',
    'tab.calls': 'المكالمات',
    'tab.orders': 'الطلبات',
    'tab.attribution': 'المصادر',

    // Timeline
    'timeline.no_events': 'لا توجد أحداث',
    'timeline.task_done': 'تم إنهاء المهمة',
    'timeline.task_cancelled': 'تم إلغاء المهمة',
    'timeline.task_created': 'تم إنشاء مهمة',
    'timeline.call_logged': 'تم تسجيل مكالمة',
    'timeline.message_sent': 'تم إرسال رسالة',
    'timeline.stage_changed': 'تم تغيير المرحلة',
    'timeline.note_added': 'تمت إضافة ملاحظة',
    'timeline.order_created': 'تم إنشاء طلب',

    // Tasks
    'task.priority.urgent': 'عاجل',
    'task.priority.high': 'مرتفع',
    'task.priority.normal': 'عادي',
    'task.priority.low': 'منخفض',
    'task.channel.call': 'اتصال',
    'task.channel.whatsapp': 'واتساب',
    'task.channel.sms': 'رسالة نصية',
    'task.due_at': 'الموعد',
    'task.add': 'إضافة مهمة',
    'task.no_tasks': 'لا توجد مهام',

    // Calls
    'call.outcome': 'النتيجة',
    'call.outcome.answered': 'تم الرد',
    'call.outcome.no_answer': 'لم يرد',
    'call.outcome.busy': 'مشغول',
    'call.outcome.voicemail': 'بريد صوتي',
    'call.outcome.wrong_number': 'رقم خاطئ',
    'call.outcome.other': 'أخرى',
    'call.duration': 'المدة (ثواني)',
    'call.minutes': 'دقيقة',
    'call.log': 'تسجيل مكالمة',
    'call.no_calls': 'لا توجد مكالمات',

    // Messages
    'message.send': 'إرسال رسالة',
    'message.template': 'قالب',
    'message.free_text': 'نص حر',
    'message.no_messages': 'لا توجد رسائل',
    'message.quick_templates': 'قوالب سريعة',

    // Attribution
    'attribution.source': 'المصدر',
    'attribution.campaign': 'الحملة',
    'attribution.medium': 'الوسيط',
    'attribution.landing_page': 'صفحة الهبوط',
    'attribution.referrer': 'المُحيل',
    'attribution.no_data': 'لا توجد بيانات attribution',
    'attribution.visits': 'ستظهر هنا زيارات العميل من الحملات الإعلانية',
    'attribution.event_type': 'نوع الحدث',
    'attribution.created': 'إنشاء',
    'attribution.visit': 'زيارة',
    'attribution.source_data': 'بيانات المصدر',
    'attribution.first_visit': 'أول زيارة',
    'attribution.visit_log': 'سجل الزيارات',

    // Orders
    'order.total': 'الإجمالي',
    'order.items': 'المنتجات',
    'order.status': 'الحالة',
    'order.date': 'التاريخ',
    'order.no_orders': 'لا توجد طلبات',
    'order.view': 'عرض الطلب',

    // Quick Actions
    'quick.log_call': 'تسجيل مكالمة',
    'quick.send_message': 'إرسال رسالة',
    'quick.add_note': 'إضافة ملاحظة',
    'quick.add_task': 'إضافة مهمة',
    'quick.templates': 'قوالب سريعة',

    // Errors
    'error.load_failed': 'فشل التحميل',
    'error.save_failed': 'فشل الحفظ',
    'error.network': 'خطأ في الشبكة',
    'error.unauthorized': 'غير مصرح',
    'error.forbidden': 'ممنوع',
    'error.not_found': 'غير موجود',
    'error.server': 'خطأ في الخادم',
    'error.login_required': 'يرجى تسجيل الدخول للمتابعة',
    'error.no_access': 'ليس لديك صلاحية الوصول',
    'error.not_found_desc': 'المحتوى غير موجود',
    'error.code': 'رمز الخطأ',
    'error.try_again': 'حاول مرة أخرى',

    // Pipeline
    'pipeline.title': 'مسار المبيعات',
    'pipeline.drag_drop': 'اسحب وأفلت العملاء بين المراحل',
    'pipeline.empty_stage': 'لا يوجد عملاء في هذه المرحلة',

    // Dashboard
    'dashboard.title': 'لوحة التحكم',
    'dashboard.total_leads': 'إجمالي العملاء',
    'dashboard.new_leads': 'عملاء جدد',
    'dashboard.conversion_rate': 'معدل التحويل',
    'dashboard.revenue': 'الإيرادات',
    'dashboard.recent_leads': 'آخر العملاء المحتملين',
    'dashboard.tasks_today': 'مهام اليوم',
    'dashboard.tasks': 'المهام',
    'dashboard.overdue': 'متأخر',
    'dashboard.today': 'اليوم',
    'dashboard.upcoming': 'قادم',
    'dashboard.no_overdue_tasks': 'لا توجد مهام متأخرة',
    'dashboard.no_tasks_today': 'لا توجد مهام لليوم',
    'dashboard.hot_leads': 'عملاء ساخنين',
    'dashboard.pipeline_overview': 'نظرة عامة على المسار',
    'dashboard.calls_today': 'مكالمات اليوم',

    // Actions (additional)
    'action.view_all': 'عرض الكل',

    // General
    'general.and': 'و',
    'general.or': 'أو',
    'general.of': 'من',
    'general.yes': 'نعم',
    'general.no': 'لا',
    'general.all': 'الكل',
    'general.none': 'لا شيء',

    // Reports Page
    'reports.title': 'التقارير والتحليلات',
    'reports.total_leads': 'إجمالي العملاء',
    'reports.converted': 'تم التحويل',
    'reports.messages_sent': 'الرسائل المرسلة',
    'reports.calls_made': 'المكالمات',
    'reports.date_range': 'النطاق الزمني',
    'reports.last_7_days': 'آخر 7 أيام',
    'reports.last_30_days': 'آخر 30 يوم',
    'reports.last_90_days': 'آخر 90 يوم',
    'reports.custom': 'مخصص',

    // Settings Page
    'settings.title': 'الإعدادات',
    'settings.general': 'عام',
    'settings.notifications': 'الإشعارات',
    'settings.profile': 'الملف الشخصي',
    'settings.language': 'اللغة',
    'settings.timezone': 'المنطقة الزمنية',
    'settings.save_changes': 'حفظ التغييرات',
    'settings.email_notifications': 'إشعارات البريد',
    'settings.push_notifications': 'الإشعارات الفورية',
    'settings.sms_alerts': 'تنبيهات SMS',

    // Templates Page
    'templates.title': 'قوالب الرسائل',
    'templates.all_channels': 'جميع القنوات',
    'templates.all_languages': 'جميع اللغات',
    'templates.no_templates': 'لا توجد قوالب',
    'templates.add_new': 'إضافة قالب',

    // Campaigns Page
    'campaigns.title': 'الحملات',
    'campaigns.all_statuses': 'جميع الحالات',
    'campaigns.active': 'نشطة',
    'campaigns.paused': 'متوقفة',
    'campaigns.draft': 'مسودة',
    'campaigns.completed': 'مكتملة',
    'campaigns.no_campaigns': 'لا توجد حملات',
    'campaigns.add_new': 'حملة جديدة',
    'campaigns.sent': 'أرسلت',
    'campaigns.opened': 'فتحت',
    'campaigns.clicked': 'نقرت',
  },

  he: {
    // Navigation
    'nav.dashboard': 'לוח בקרה',
    'nav.leads': 'לידים',
    'nav.pipeline': 'צינור מכירות',
    'nav.products': 'מוצרים',
    'nav.orders': 'הזמנות',
    'nav.campaigns': 'קמפיינים',
    'nav.templates': 'תבניות',
    'nav.messages': 'הודעות',
    'nav.calls': 'שיחות',
    'nav.reports': 'דוחות',
    'nav.settings': 'הגדרות',
    'nav.logout': 'התנתק',

    // Login
    'login.logging_in': 'מתחבר...',
    'login.success': 'התחברות הצליחה!',
    'login.failed': 'התחברות נכשלה',
    'login.error': 'שגיאת חיבור',
    'login.subtitle': 'ניהול קשרי לקוחות',

    // Actions
    'action.save': 'שמור',
    'action.cancel': 'ביטול',
    'action.delete': 'מחק',
    'action.edit': 'ערוך',
    'action.add': 'הוסף',
    'action.search': 'חיפוש',
    'action.filter': 'סינון',
    'action.export': 'ייצוא',
    'action.import': 'ייבוא',
    'action.refresh': 'רענן',
    'action.send': 'שלח',
    'action.call': 'התקשר',
    'action.whatsapp': 'וואטסאפ',
    'action.back': 'חזור',
    'action.view': 'צפה',
    'action.open': 'פתח',
    'action.done': 'בוצע',
    'action.retry': 'נסה שוב',
    'action.add_lead': 'הוסף ליד',
    'action.copy_phone': 'העתק טלפון',

    // Leads page
    'leads.search_placeholder': 'חיפוש לפי שם או טלפון...',
    'leads.no_leads': 'לא נמצאו לידים',
    'leads.add_first': 'הוסף ליד ראשון',
    'leads.add_new': 'הוסף ליד חדש',
    'leads.last_activity': 'פעילות אחרונה',
    'leads.actions': 'פעולות',
    'leads.no_name': 'ללא שם',

    // Sources
    'source.website': 'אתר',
    'source.whatsapp': 'וואטסאפ',
    'source.referral': 'הפניה',
    'source.ad': 'פרסום',
    'source.other': 'אחר',

    // Stages
    'stage.all': 'כל השלבים',
    'stage.new': 'חדש',
    'stage.contacted': 'נוצר קשר',
    'stage.qualified': 'מאושר',
    'stage.proposal': 'הצעת מחיר',
    'stage.won': 'נסגר בהצלחה',
    'stage.lost': 'אבד',

    // Status / States
    'status.loading': 'טוען...',
    'status.error': 'שגיאה',
    'status.empty': 'אין נתונים',
    'status.open': 'פתוח',
    'status.done': 'הושלם',
    'status.cancelled': 'בוטל',
    'status.sent': 'נשלח',
    'status.delivered': 'נמסר',
    'status.read': 'נקרא',
    'status.failed': 'נכשל',
    'status.pending': 'ממתין',
    'status.preview': 'תצוגה מקדימה',
    'status.overdue': 'באיחור',

    // Forms
    'form.name': 'שם',
    'form.phone': 'טלפון',
    'form.email': 'אימייל',
    'form.notes': 'הערות',
    'form.required': 'שדה חובה',
    'form.title': 'כותרת',
    'form.description': 'תיאור',
    'form.date': 'תאריך',
    'form.time': 'שעה',
    'form.select': 'בחר...',

    // Messages / Toasts
    'msg.saved': 'נשמר בהצלחה',
    'msg.deleted': 'נמחק בהצלחה',
    'msg.error': 'שגיאה, נסה שוב',
    'msg.confirm_delete': 'האם אתה בטוח שברצונך למחוק?',
    'msg.task_added': 'משימה נוספה בהצלחה',
    'msg.task_done': 'משימה הושלמה',
    'msg.task_cancelled': 'משימה בוטלה',
    'msg.message_sent': 'ההודעה נשלחה',
    'msg.call_logged': 'שיחה נרשמה',
    'msg.note_added': 'הערה נוספה',
    'msg.stage_updated': 'שלב עודכן',
    'msg.lead_added': 'ליד נוסף בהצלחה',
    'msg.lead_add_failed': 'הוספת ליד נכשלה',
    'msg.copied': 'הועתק',
    'msg.auto_message_sent_today': 'הודעה אוטומטית כבר נשלחה היום',
    'msg.auto_message_scheduled': 'הודעה אוטומטית תוזמנה',

    // Lead Detail
    'lead.potential_customer': 'לקוח פוטנציאלי',
    'lead.contact_whatsapp': 'יצירת קשר בוואטסאפ',
    'lead.quick_followup': 'מעקב מהיר',
    'lead.info': 'פרטי הליד',
    'lead.phone': 'טלפון',
    'lead.email': 'אימייל',
    'lead.source': 'מקור',
    'lead.stage': 'שלב',
    'lead.created_at': 'נוצר',
    'lead.updated_at': 'עודכן',
    'lead.value': 'ערך',
    'lead.notes': 'הערות',
    'lead.no_leads': 'אין לידים',
    'lead.load_error': 'טעינת נתוני הליד נכשלה',
    'lead.not_found': 'ליד לא נמצא',
    'lead.not_found_desc': 'לא ניתן למצוא ליד זה',

    // Tabs
    'tab.timeline': 'ציר זמן',
    'tab.notes': 'הערות',
    'tab.tasks': 'משימות',
    'tab.messages': 'הודעות',
    'tab.calls': 'שיחות',
    'tab.orders': 'הזמנות',
    'tab.attribution': 'ייחוס',

    // Timeline
    'timeline.no_events': 'אין אירועים',
    'timeline.task_done': 'משימה הושלמה',
    'timeline.task_cancelled': 'משימה בוטלה',
    'timeline.task_created': 'משימה נוצרה',
    'timeline.call_logged': 'שיחה נרשמה',
    'timeline.message_sent': 'הודעה נשלחה',
    'timeline.stage_changed': 'שלב השתנה',
    'timeline.note_added': 'הערה נוספה',
    'timeline.order_created': 'הזמנה נוצרה',

    // Tasks
    'task.priority.urgent': 'דחוף',
    'task.priority.high': 'גבוה',
    'task.priority.normal': 'רגיל',
    'task.priority.low': 'נמוך',
    'task.channel.call': 'שיחה',
    'task.channel.whatsapp': 'וואטסאפ',
    'task.channel.sms': 'SMS',
    'task.due_at': 'תאריך יעד',
    'task.add': 'הוסף משימה',
    'task.no_tasks': 'אין משימות',

    // Calls
    'call.outcome': 'תוצאה',
    'call.outcome.answered': 'נענתה',
    'call.outcome.no_answer': 'לא נענה',
    'call.outcome.busy': 'תפוס',
    'call.outcome.voicemail': 'תא קולי',
    'call.outcome.wrong_number': 'מספר שגוי',
    'call.outcome.other': 'אחר',
    'call.duration': 'משך (שניות)',
    'call.minutes': 'דקות',
    'call.log': 'רשום שיחה',
    'call.no_calls': 'אין שיחות',

    // Messages
    'message.send': 'שלח הודעה',
    'message.template': 'תבנית',
    'message.free_text': 'טקסט חופשי',
    'message.no_messages': 'אין הודעות',
    'message.quick_templates': 'תבניות מהירות',

    // Attribution
    'attribution.source': 'מקור',
    'attribution.campaign': 'קמפיין',
    'attribution.medium': 'מדיום',
    'attribution.landing_page': 'דף נחיתה',
    'attribution.referrer': 'מפנה',
    'attribution.no_data': 'אין נתוני ייחוס',
    'attribution.visits': 'ביקורים מקמפיינים יופיעו כאן',
    'attribution.event_type': 'סוג אירוע',
    'attribution.created': 'נוצר',
    'attribution.visit': 'ביקור',
    'attribution.source_data': 'נתוני מקור',
    'attribution.first_visit': 'ביקור ראשון',
    'attribution.visit_log': 'יומן ביקורים',

    // Orders
    'order.total': 'סה"כ',
    'order.items': 'פריטים',
    'order.status': 'סטטוס',
    'order.date': 'תאריך',
    'order.no_orders': 'אין הזמנות',
    'order.view': 'צפה בהזמנה',

    // Quick Actions
    'quick.log_call': 'רשום שיחה',
    'quick.send_message': 'שלח הודעה',
    'quick.add_note': 'הוסף הערה',
    'quick.add_task': 'הוסף משימה',
    'quick.templates': 'תבניות מהירות',

    // Errors
    'error.load_failed': 'הטעינה נכשלה',
    'error.save_failed': 'השמירה נכשלה',
    'error.network': 'שגיאת רשת',
    'error.unauthorized': 'לא מורשה',
    'error.forbidden': 'אסור',
    'error.not_found': 'לא נמצא',
    'error.server': 'שגיאת שרת',
    'error.login_required': 'אנא התחבר כדי להמשיך',
    'error.no_access': 'אין לך הרשאת גישה',
    'error.not_found_desc': 'התוכן לא נמצא',
    'error.code': 'קוד שגיאה',
    'error.try_again': 'נסה שוב',

    // Pipeline
    'pipeline.title': 'צינור מכירות',
    'pipeline.drag_drop': 'גרור ושחרר לידים בין שלבים',
    'pipeline.empty_stage': 'אין לידים בשלב זה',

    // Dashboard
    'dashboard.title': 'לוח בקרה',
    'dashboard.total_leads': 'סה"כ לידים',
    'dashboard.new_leads': 'לידים חדשים',
    'dashboard.conversion_rate': 'אחוז המרה',
    'dashboard.revenue': 'הכנסות',
    'dashboard.recent_leads': 'לידים אחרונים',
    'dashboard.tasks_today': 'משימות להיום',
    'dashboard.tasks': 'משימות',
    'dashboard.overdue': 'באיחור',
    'dashboard.today': 'היום',
    'dashboard.upcoming': 'בקרוב',
    'dashboard.no_overdue_tasks': 'אין משימות באיחור',
    'dashboard.no_tasks_today': 'אין משימות להיום',
    'dashboard.hot_leads': 'לידים חמים',
    'dashboard.pipeline_overview': 'סקירת צינור',
    'dashboard.calls_today': 'שיחות היום',

    // Actions (additional)
    'action.view_all': 'הצג הכל',

    // General
    'general.and': 'ו',
    'general.or': 'או',
    'general.of': 'מתוך',
    'general.yes': 'כן',
    'general.no': 'לא',
    'general.all': 'הכל',
    'general.none': 'כלום',

    // Reports Page
    'reports.title': 'דוחות ואנליטיקה',
    'reports.total_leads': 'סה"כ לידים',
    'reports.converted': 'הומרו',
    'reports.messages_sent': 'הודעות נשלחו',
    'reports.calls_made': 'שיחות',
    'reports.date_range': 'טווח תאריכים',
    'reports.last_7_days': '7 ימים אחרונים',
    'reports.last_30_days': '30 ימים אחרונים',
    'reports.last_90_days': '90 ימים אחרונים',
    'reports.custom': 'מותאם אישית',

    // Settings Page
    'settings.title': 'הגדרות',
    'settings.general': 'כללי',
    'settings.notifications': 'התראות',
    'settings.profile': 'פרופיל',
    'settings.language': 'שפה',
    'settings.timezone': 'אזור זמן',
    'settings.save_changes': 'שמור שינויים',
    'settings.email_notifications': 'התראות אימייל',
    'settings.push_notifications': 'התראות פוש',
    'settings.sms_alerts': 'התראות SMS',

    // Templates Page
    'templates.title': 'תבניות הודעות',
    'templates.all_channels': 'כל הערוצים',
    'templates.all_languages': 'כל השפות',
    'templates.no_templates': 'לא נמצאו תבניות',
    'templates.add_new': 'הוסף תבנית',

    // Campaigns Page
    'campaigns.title': 'קמפיינים',
    'campaigns.all_statuses': 'כל הסטטוסים',
    'campaigns.active': 'פעיל',
    'campaigns.paused': 'מושהה',
    'campaigns.draft': 'טיוטה',
    'campaigns.completed': 'הושלם',
    'campaigns.no_campaigns': 'לא נמצאו קמפיינים',
    'campaigns.add_new': 'קמפיין חדש',
    'campaigns.sent': 'נשלחו',
    'campaigns.opened': 'נפתחו',
    'campaigns.clicked': 'נלחצו',
  },
};

let currentLang: Lang = 'en';

/**
 * Set current language for translation lookups
 */
export function setCurrentLang(lang: Lang): void {
  currentLang = lang;
}

/**
 * Get current language
 */
export function getCurrentLang(): Lang {
  return currentLang;
}

/**
 * Get translated string for a key
 * Falls back to English if not found, then to key itself
 */
export function t(key: string, lang?: Lang): string {
  const useLang = lang ?? currentLang;
  return translations[useLang]?.[key] ?? translations['en']?.[key] ?? key;
}

/**
 * Get all translations for a language
 */
export function getTranslations(lang: Lang): TranslationDict {
  return translations[lang] ?? translations['en'];
}

/**
 * Format date according to locale
 */
export function formatDate(date: Date | string, format?: 'date' | 'time' | 'datetime', lang?: Lang): string {
  const useLang = lang ?? currentLang;
  const d = typeof date === 'string' ? new Date(date.replace(' ', 'T')) : date;
  
  const localeMap: Record<Lang, string> = {
    en: 'en-US',
    ar: 'ar-EG',
    he: 'he-IL',
  };
  
  const options: Intl.DateTimeFormatOptions = format === 'date' 
    ? { year: 'numeric', month: 'short', day: 'numeric' }
    : format === 'time'
    ? { hour: '2-digit', minute: '2-digit' }
    : { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
  
  return d.toLocaleString(localeMap[useLang], options);
}

/**
 * Format number according to locale
 */
export function formatNumber(num: number, lang?: Lang): string {
  const useLang = lang ?? currentLang;
  
  const localeMap: Record<Lang, string> = {
    en: 'en-US',
    ar: 'ar-EG',
    he: 'he-IL',
  };
  
  return num.toLocaleString(localeMap[useLang]);
}

/**
 * Format currency according to locale
 */
export function formatCurrency(amount: number, currency = 'ILS', lang?: Lang): string {
  const useLang = lang ?? currentLang;
  
  const localeMap: Record<Lang, string> = {
    en: 'en-IL',
    ar: 'ar-IL',
    he: 'he-IL',
  };
  
  return new Intl.NumberFormat(localeMap[useLang], {
    style: 'currency',
    currency,
  }).format(amount);
}
